<template>
  <ApexChart v-if="chartOptions" element="bunkerSummaryChart" :chartOption="chartOptions" :series="chartOptions.series"
    class="mt-5" />
</template>

<script>
import moment from 'moment';

export default {
  name: 'BunkerSummaryChart',
  props: {
    mappedSeries: {
      type: Array,
      required: true,
    },
  },
  computed: {
    chartOptions() {
      if (!this.mappedSeries || this.mappedSeries.length === 0) {
        return null;
      }

      const isMobile = window.innerWidth <= 991;

      // 1. Ekstraksi data dengan penanganan null
      const getSafeData = (data, key) => {
        const raw = data?.fmData?.BUNKER_IN?.[key];
        if (raw !== null && typeof raw !== 'undefined' && Number.isFinite(Number(raw))) {
          return Number(Number(raw).toFixed(2));
        }
        return null;
      };

      const MappedMassTotal = this.mappedSeries.map((data) => getSafeData(data, 'massTotal'));
      const MappedMassFlowrate = this.mappedSeries.map((data) => getSafeData(data, 'massFlowrate'));
      const MappedVolumeTotal = this.mappedSeries.map((data) => getSafeData(data, 'volumeTotal'));
      const MappedVolumeFlowrate = this.mappedSeries.map((data) => getSafeData(data, 'volumeFlowrate'));
      const MappedTemperature = this.mappedSeries.map((data) => getSafeData(data, 'temperature'));
      const MappedDensity = this.mappedSeries.map((data) => getSafeData(data, 'density'));

      // 2. Terapkan interpolasi
      const InterpolatedMassTotal = this.interpolateNulls(MappedMassTotal);
      const InterpolatedMassFlowrate = this.interpolateNulls(MappedMassFlowrate);
      const InterpolatedVolumeTotal = this.interpolateNulls(MappedVolumeTotal);
      const InterpolatedVolumeFlowrate = this.interpolateNulls(MappedVolumeFlowrate);
      const InterpolatedTemperature = this.interpolateNulls(MappedTemperature);
      const InterpolatedDensity = this.interpolateNulls(MappedDensity);

      const seriesNames = {
        massTotal: 'Mass Total',
        massFlowrate: 'Mass Flowrate',
        volumeTotal: 'Volume Total',
        volumeFlowrate: 'Volume Flowrate',
        temperature: 'Temperature',
        density: 'Density',
      };

      const seriesData = [
        { name: seriesNames.massTotal, data: MappedMassTotal, color: '#00CA00' },
        { name: `${seriesNames.massTotal} (Interpolated)`, data: InterpolatedMassTotal, color: 'rgba(0, 202, 0, 0.4)' },
        { name: seriesNames.massFlowrate, data: MappedMassFlowrate, color: '#0084ff' },
        { name: `${seriesNames.massFlowrate} (Interpolated)`, data: InterpolatedMassFlowrate, color: 'rgba(0, 132, 255, 0.4)' },
        { name: seriesNames.volumeTotal, data: MappedVolumeTotal, color: '#00E096' },
        { name: `${seriesNames.volumeTotal} (Interpolated)`, data: InterpolatedVolumeTotal, color: 'rgba(0, 224, 150, 0.4)' },
        { name: seriesNames.volumeFlowrate, data: MappedVolumeFlowrate, color: '#F94144' },
        { name: `${seriesNames.volumeFlowrate} (Interpolated)`, data: InterpolatedVolumeFlowrate, color: 'rgba(249, 65, 68, 0.4)' },
        { name: seriesNames.temperature, data: MappedTemperature, color: '#FFD400' },
        { name: `${seriesNames.temperature} (Interpolated)`, data: InterpolatedTemperature, color: 'rgba(255, 212, 0, 0.4)' },
        { name: seriesNames.density, data: MappedDensity, color: '#B141E6' },
        { name: `${seriesNames.density} (Interpolated)`, data: InterpolatedDensity, color: 'rgba(177, 65, 230, 0.4)' },
      ];

      const legendFillColors = seriesData.map((s) => (s.name.includes('(Interpolated)') ? 'transparent' : s.color));

      return {
        series: seriesData,
        chart: {
          height: 500,
          type: 'line',
          stacked: false,
          animations: {
            enabled: false,
          },
          events: {
            legendClick(chartContext, seriesIndex) {
              const seriesName = chartContext.w.globals.seriesNames[seriesIndex];
              const interpolatedName = `${seriesName} (Interpolated)`;
              chartContext.toggleSeries(interpolatedName);
            },
          },
        },
        markers: {
          size: 3,
        },
        stroke: {
          width: [3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2],
          dashArray: [0, 8, 0, 8, 0, 8, 0, 8, 0, 8, 0, 8],
        },
        legend: {
          position: 'bottom',
          formatter(seriesName) {
            if (seriesName.includes('(Interpolated)')) {
              return '';
            }
            return seriesName;
          },
          markers: {
            fillColors: legendFillColors,
          },
        },
        tooltip: {
          shared: true,
          // eslint-disable-next-line no-unused-vars, object-curly-newline
          custom({ series, seriesIndex, dataPointIndex, w }) {
            const header = w.globals.categoryLabels[dataPointIndex];
            let tooltipContent = '';

            for (let i = 0; i < series.length; i += 1) {
              if (!w.globals.seriesNames[i].includes('(Interpolated)')) {
                const seriesValue = series[i][dataPointIndex];
                if (seriesValue !== null && typeof seriesValue !== 'undefined') {
                  const seriesColor = w.globals.colors[i];
                  const seriesName = w.globals.seriesNames[i];
                  tooltipContent += `
                        <div class="apexcharts-tooltip-series-group" style="order: ${i}; display: flex;">
                        <span class="apexcharts-tooltip-marker" style="background-color: ${seriesColor};"></span>
                        <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                            <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${seriesName}: </span>
                            <span class="apexcharts-tooltip-text-y-value">${seriesValue}</span>
                            </div>
                        </div>
                        </div>`;
                }
              }
            }

            if (tooltipContent === '') {
              return '';
            }

            return `
                <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                    ${header}
                </div>
                <div class="apexcharts-tooltip-series-group-wrapper" style="display: flex; flex-direction: column;">
                    ${tooltipContent}
                </div>`;
          },
        },
        xaxis: {
          type: 'datetime',
          labels: {
            formatter(val) {
              return moment(val).format('DD-MM-YYYY HH:mm');
            },
          },
          categories: this.mappedSeries.map((data) => moment(data.timestamp * 1000).format('DD MMMM YYYY HH:mm')),
          tickAmount: isMobile ? 3 : undefined,
        },
        yaxis: [
          { title: { text: 'Mass Total' }, show: !isMobile },
          { show: false }, // for interpolated
          { title: { text: 'Mass Flowrate' }, show: !isMobile },
          { show: false }, // for interpolated
          { opposite: true, title: { text: 'Volume Total' }, show: !isMobile },
          { opposite: true, show: false }, // for interpolated
          { opposite: true, title: { text: 'Volume Flowrate' }, show: !isMobile },
          { opposite: true, show: false }, // for interpolated
          { opposite: true, title: { text: 'Density' }, show: !isMobile },
          { opposite: true, show: false }, // for interpolated
          { opposite: true, title: { text: 'Temperature' }, show: !isMobile },
          { opposite: true, show: false }, // for interpolated
        ],
      };
    },
  },
  methods: {
    interpolateNulls(dataArray) {
      const result = [...dataArray];
      for (let i = 0; i < result.length; i += 1) {
        if (result[i] === null) {
          const startIndex = i - 1;
          const startValue = startIndex >= 0 ? result[startIndex] : null;

          if (startValue !== null) {
            let endIndex = -1;
            for (let j = i + 1; j < result.length; j += 1) {
              if (result[j] !== null) {
                endIndex = j;
                break;
              }
            }

            if (endIndex !== -1) {
              const endValue = result[endIndex];
              const gapCount = endIndex - startIndex;
              const totalDifference = endValue - startValue;
              const step = totalDifference / gapCount;

              for (let k = startIndex + 1; k < endIndex; k += 1) {
                const interpolatedValue = startValue + step * (k - startIndex);
                result[k] = Number(interpolatedValue.toFixed(2));
              }
              i = endIndex - 1;
            }
          }
        }
      }
      return result;
    },
  },
};
</script>
